import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ChildsEducationPrediction } from '@core/models/coinscrap/childs-education-prediction.model';
import { DeathProtectionPrediction } from '@core/models/coinscrap/death-prediction.model';
import { FinancialAdvice } from '@core/models/coinscrap/financial-advice.model';
import { FinancialPrediction } from '@core/models/coinscrap/financial-prediction.model';
import { FutureGoalsPrediction } from '@core/models/coinscrap/future-goals-prediction.model';
import { extractText } from '@core/models/coinscrap/i18n-message';
import { monetaryAmountToCurrencyAmount } from '@core/models/coinscrap/monetary-amount.model';
import { OfferingProduct } from '@core/models/coinscrap/offering-product.model';
import { periodicAmountToMonthlyCurrencyAmount } from '@core/models/coinscrap/periodic-amount.model';
import { Rating } from '@core/models/coinscrap/rating.model';
import {
  FUTURE_GOALS_PURPOSE_SAFETY_NET_SUFFIX,
  generateReportsRequest,
  ReportsResponse,
  SPOUSE_IDENTIFIER,
  USER_IDENTIFIER,
} from '@core/models/coinscrap/reports.model';
import { RetirementPrediction } from '@core/models/coinscrap/retirement-prediction.model';
import { Config } from '@core/models/config.model';
import {
  CurrencyAmount,
  savingFeeCurrencyAmount,
} from '@core/models/currency-amount.model';
import { AppError } from '@core/models/error.model';
import {
  ConcreteInsuranceId,
  ConcreteInsuranceIds,
} from '@core/models/insurance.model';
import { FuturePlanData, LifePlan } from '@core/models/life-plan.model';
import {
  adjustProductDataToMatchTemporality,
  NormalProduct,
} from '@core/models/product.model';
import {
  generateProductsRecommendationsPerTargetFromOfferingProduct,
  ProductsPerInsuranceTarget,
} from '@core/models/products-per-insurance-target.model';
import { environment } from '@environment';
import { LanguageService } from '@i18n/services/language.service';
import { Store } from '@ngrx/store';
import * as actions from '@redux/actions';
import { fullUpdateRatings } from '@redux/actions';
import { fullUpdateRetirementInfo } from '@redux/actions/retirement-info.actions';
import { AppState } from '@redux/app.reducers';
import {
  FinancialInfo,
  initialFinancialInfo,
  initialPredictionsState,
  PredictionsState,
  RatingsState,
  RetirementInfo,
} from '@redux/reducers';
import { map } from 'rxjs/operators';

/**
 * This service provides communication with the Ratings endpoint and manages both,
 * the default life plan configuration that comes from the endpoint and the finnancial
 * health information that comes along the default life plan configuration.
 */
@Injectable({
  providedIn: 'root',
})
export class ReportsService {
  config!: Config;
  futurePlans!: FuturePlanData[];

  constructor(
    private store: Store<AppState>,
    private http: HttpClient,
    private languageService: LanguageService
  ) {
    this.store.subscribe({
      next: (state) => {
        this.config = state.config;
        this.futurePlans =
          state.lifePlan.currentLifePlan.user?.financial?.futurePlans ?? [];
      },
    });
  }

  getAndUpdateReportsData(lifePlan: LifePlan) {
    const apiRequest = generateReportsRequest(lifePlan, this.config);
    if (
      !apiRequest.futureGoals?.find(
        (f) =>
          f.purpose === `futureGoals/${FUTURE_GOALS_PURPOSE_SAFETY_NET_SUFFIX}`
      )
    ) {
      apiRequest.futureGoals?.push({
        identifier: 'saving-general',
        purpose: `futureGoals/${FUTURE_GOALS_PURPOSE_SAFETY_NET_SUFFIX}`,
      });
    }
    return this.http
      .post<ReportsResponse>(
        `${environment.scorings.url}/reports/scorings`,
        apiRequest
      )
      .pipe(
        map((apiResponse) => {
          if (apiResponse.errorMessage) {
            console.error(
              'Unknown error on Reports API response: ',
              apiResponse
            );
            const errorTypeRegex = /\((\d{3})\)/;
            const match = apiResponse.errorMessage.match(errorTypeRegex);
            const errorType = match ? match[1] : null;

            if (errorType == '400') {
              const messageAfterColonRegex = /:(.+)/;
              const match = apiResponse.errorMessage.match(
                messageAfterColonRegex
              );
              const message = match ? match[1].trim() : null;
              throw new Error(message);
            } else throw new Error('Unknown error on Reports API response');
          }
          if (
            apiResponse._metadata.retirementPrediction.retirementLawMetadata
              .warnings.length > 0
          ) {
            const retirementInfo: RetirementInfo = { error: true };
            this.store.dispatch(
              fullUpdateRetirementInfo({ newretirementInfo: retirementInfo })
            );
          }
          return {
            apiResponse,
          };
        }),
        map((reportsData) => {
          const newRatings = this.extractRatingsAndDispatch(
            reportsData.apiResponse
          );
          return {
            ...reportsData,
            ratings: newRatings,
          };
        }),
        map((reportsData) => {
          const products = this.extractProductsPerInsuranceTargetAndDispatch(
            reportsData.apiResponse,
            lifePlan
          );
          return {
            ...reportsData,
            productsPerInsuranceTarget: products,
          };
        }),
        map((reportsData) => {
          const financialHealth = this.extractFinancialInfoAndDispatch(
            reportsData.apiResponse
          );
          return {
            ...reportsData,
            financialHealth,
          };
        }),
        map((reportsData) => {
          const predictions = this.extractPredictionsAndDispatch(
            reportsData.apiResponse
          );
          return {
            ...reportsData,
            predictions,
          };
        }),
        // Clone the reports data since it is being stored on Redux
        map((reportsData) => JSON.parse(JSON.stringify(reportsData)))
      );
  }

  private extractRatingsAndDispatch(
    apiResponse: ReportsResponse
  ): RatingsState {
    const newRatingsState = this.initializeRatingsState();
    this.populateRatingsState(
      newRatingsState,
      apiResponse.globalRating,
      'current'
    );
    this.populateRatingsState(
      newRatingsState,
      apiResponse.globalRatingWithoutProducts,
      'default'
    );
    this.validateRatingsState(
      newRatingsState,
      apiResponse.globalRating,
      apiResponse.globalRatingWithoutProducts
    );
    this.store.dispatch(fullUpdateRatings({ newRatingsState }));
    return newRatingsState;
  }

  private initializeRatingsState(): RatingsState {
    return {
      global: {
        current: -1,
        default: -1,
      },
      financial: {
        current: -1,
        default: -1,
      },
      protections: {
        DEATH: {
          current: -1,
          default: -1,
        },
        EDUCATION: {
          current: -1,
          default: -1,
        },
        RETIREMENT: {
          current: -1,
          default: -1,
        },
        SAVING: {
          current: -1,
          default: -1,
        },
        DECUMULATION: {
          current: -1,
          default: -1,
        },
      },
      insuranceTargets: {},
    };
  }

  private populateRatingsState(
    ratingsState: RatingsState,
    rating: Rating,
    ratingStatus: 'current' | 'default'
  ) {
    if (!rating) {
      return;
    }
    ratingsState.global[ratingStatus] = Math.round(rating.value);
    rating.ratings.forEach((innerRating) => {
      switch (innerRating.type) {
        case 'DEATH_PROTECTION':
          ratingsState.protections.DEATH[ratingStatus] = Math.round(
            innerRating.value
          );
          break;
        case 'CHILDS_EDUCATION_SAVINGS':
          ratingsState.protections.EDUCATION[ratingStatus] = Math.round(
            innerRating.value
          );
          break;
        case 'RETIREMENT_SAVINGS':
          ratingsState.protections.RETIREMENT[ratingStatus] = Math.round(
            innerRating.value
          );
          break;
        case 'FUTURE_GOALS':
          ratingsState.protections.SAVING[ratingStatus] = Math.round(
            innerRating.value
          );
          break;
        case 'FINANCIAL':
          ratingsState.financial[ratingStatus] = Math.round(innerRating.value);
          break;
        default:
          console.error(
            `Unknown protection rating ${innerRating.type} on rating ${
              ratingStatus === 'current' ? 'global' : 'without products'
            }`
          );
      }
      if (innerRating.ratings) {
        innerRating.ratings.forEach((insuranceTargetRating) => {
          let key;
          if (
            innerRating.type === 'DEATH_PROTECTION' ||
            innerRating.type === 'RETIREMENT_SAVINGS'
          ) {
            switch (insuranceTargetRating.type) {
              case USER_IDENTIFIER:
                key =
                  innerRating.type === 'DEATH_PROTECTION'
                    ? ConcreteInsuranceIds.DEATH_USER
                    : ConcreteInsuranceIds.RETIREMENT_USER;
                break;
              case SPOUSE_IDENTIFIER:
                key =
                  innerRating.type === 'DEATH_PROTECTION'
                    ? ConcreteInsuranceIds.DEATH_SPOUSE
                    : ConcreteInsuranceIds.RETIREMENT_SPOUSE;
                break;
              default:
                console.error(
                  `Unknown insurance rating type ${insuranceTargetRating.type} on protection rating ${innerRating.type}`
                );
                return;
            }
          } else {
            key = insuranceTargetRating.type;
          }
          if (!ratingsState.insuranceTargets[key]) {
            ratingsState.insuranceTargets[key] = {
              current: -1,
              default: -1,
            };
          }
          ratingsState.insuranceTargets[key][ratingStatus] = Math.round(
            insuranceTargetRating.value
          );
        });
      }
    });
  }

  private validateRatingsState(
    ratingsState: RatingsState,
    globalRating: Rating,
    globalRatingWithoutProducts: Rating
  ) {
    const errorRatings = [];
    if (ratingsState.global.current < 0 || ratingsState.global.default < 0) {
      errorRatings.push('global');
      if (ratingsState.global.current < 0) {
        ratingsState.global.current = 0;
      }
      if (ratingsState.global.default < 0) {
        ratingsState.global.default = 0;
      }
    }
    Object.entries(ratingsState.protections)
      .concat(Object.entries(ratingsState.insuranceTargets))
      .forEach((e) => {
        // Check if both ratings have the same simbol (i.e. both come in api response or not)
        if (e[1].current * e[1].default < 0) {
          errorRatings.push(e[0]);
        }
        if (e[1].current < 0) {
          e[1].current = 0;
        }
        if (e[1].default < 0) {
          e[1].default = 0;
        }
      });

    if (errorRatings.length > 0) {
      console.error(
        `Errors detected validating ratings ${JSON.stringify(
          errorRatings
        )}. GlobalRating: ${JSON.stringify(
          globalRating
        )}, GlobalRatingWithoutProducts: ${JSON.stringify(
          globalRatingWithoutProducts
        )}`
      );
    }
  }

  private extractProductsPerInsuranceTargetAndDispatch(
    apiResponse: ReportsResponse,
    lifePlan: LifePlan
  ): ProductsPerInsuranceTarget {
    const productsPerInsuranceTarget: ProductsPerInsuranceTarget = {};
    if (apiResponse.deathProtectionOfferingProducts) {
      productsPerInsuranceTarget[ConcreteInsuranceIds.DEATH_USER] =
        generateProductsRecommendationsPerTargetFromOfferingProduct(
          ConcreteInsuranceIds.DEATH_USER,
          apiResponse.deathProtectionOfferingProducts,
          this.config,
          lifePlan
        );
    }
    if (apiResponse.partnerDeathProtectionOfferingProducts) {
      productsPerInsuranceTarget[ConcreteInsuranceIds.DEATH_SPOUSE] =
        generateProductsRecommendationsPerTargetFromOfferingProduct(
          ConcreteInsuranceIds.DEATH_SPOUSE,
          apiResponse.partnerDeathProtectionOfferingProducts,
          this.config,
          lifePlan
        );
    }
    if (apiResponse.childsEducationOfferingProducts) {
      const childsGroupedOfferingProducts =
        apiResponse.childsEducationOfferingProducts.reduce((r, p) => {
          const id = p.person?.identifier;
          if (!id) {
            const error =
              'Child identifier not found or is empty while trying to get offering products!';
            console.error(error);
            throw new Error(error);
          }
          if (!r[id]) {
            r[id] = [];
          }
          r[id].push(p);
          return r;
        }, Object.create(null)) as { [key: string]: OfferingProduct[] };
      Object.entries(childsGroupedOfferingProducts).forEach(
        ([id, offeringProducts]) => {
          productsPerInsuranceTarget[id] =
            generateProductsRecommendationsPerTargetFromOfferingProduct(
              id,
              offeringProducts,
              this.config,
              lifePlan
            );
        }
      );
    }
    if (apiResponse.futureGoalsOfferingProducts) {
      const futureGoalsGroupedOfferingProducts =
        apiResponse.futureGoalsOfferingProducts.reduce((r, p) => {
          const id = p.futureGoal?.identifier;
          if (!id) {
            const error =
              'Future goal identifier not found or is empty while trying to get offering products!';
            console.error(error);
            throw new Error(error);
          }
          if (!r[id]) {
            r[id] = [];
          }
          r[id].push(p);
          return r;
        }, Object.create(null)) as { [key: string]: OfferingProduct[] };
      Object.entries(futureGoalsGroupedOfferingProducts).forEach(
        ([id, offeringProducts]) => {
          productsPerInsuranceTarget[id] =
            generateProductsRecommendationsPerTargetFromOfferingProduct(
              id,
              offeringProducts,
              this.config,
              lifePlan
            );
          if (id !== 'saving-general') {
            const product = productsPerInsuranceTarget[id]
              .recommended as NormalProduct;
            const currentAssuredAmount = {
              amount: offeringProducts[0].futureGoal?.currentAmount?.value!,
              currency:
                offeringProducts[0].futureGoal?.currentAmount?.currency!,
            };
            product.currentAssuredAmount = currentAssuredAmount;
            const endDate = new Date(
              offeringProducts[0].futureGoal?.endDate!
            ).getFullYear();
            const startDate = new Date(
              this.futurePlans.find((fp) => fp.id === id)!.startDate!
            ).getFullYear();
            const goalAge = endDate - startDate;
            product.currentFee = savingFeeCurrencyAmount(
              currentAssuredAmount,
              goalAge
            );
            productsPerInsuranceTarget[id].recommended = product;
          }
        }
      );
    }
    if (apiResponse.retirementOfferingProducts) {
      productsPerInsuranceTarget[ConcreteInsuranceIds.RETIREMENT_USER] =
        generateProductsRecommendationsPerTargetFromOfferingProduct(
          ConcreteInsuranceIds.RETIREMENT_USER,
          apiResponse.retirementOfferingProducts,
          this.config,
          lifePlan
        );
    }
    if (apiResponse.partnerRetirementOfferingProducts) {
      productsPerInsuranceTarget[ConcreteInsuranceIds.RETIREMENT_SPOUSE] =
        generateProductsRecommendationsPerTargetFromOfferingProduct(
          ConcreteInsuranceIds.RETIREMENT_SPOUSE,
          apiResponse.partnerRetirementOfferingProducts,
          this.config,
          lifePlan
        );
    }

    this.adjustProductsDataToMatchTemporality(
      productsPerInsuranceTarget,
      lifePlan
    );
    this.store.dispatch(
      actions.updateProductsPerInsuranceTarget({
        newProductsPerInsuranceTarget: productsPerInsuranceTarget,
      })
    );
    return productsPerInsuranceTarget;
  }

  private extractFinancialInfoAndDispatch(apiResponse: ReportsResponse): any {
    const financialInfo: FinancialInfo = JSON.parse(
      JSON.stringify(initialFinancialInfo)
    );
    this.populateFinancialPredictions(
      financialInfo,
      apiResponse.financialPrediction
    );
    this.populateFinancialAdvices(financialInfo, apiResponse.financialAdvices);
    this.store.dispatch(
      actions.fullUpdateFinancialInfo({ newfinancialInfo: financialInfo })
    );
    return financialInfo;
  }

  private populateFinancialPredictions(
    financialInfo: FinancialInfo,
    financialPrediction: FinancialPrediction
  ) {
    if (!financialPrediction?.now) {
      return;
    }
    financialInfo.features.monthlyIncomes.currencyAmount =
      periodicAmountToMonthlyCurrencyAmount(
        financialPrediction.now.incomesAmount,
        this.config.currencies
      );
    financialInfo.features.monthlyExpenses.percentage =
      financialPrediction.now.expensesPercentage;
    financialInfo.features.savingCapacityChart.incomes =
      periodicAmountToMonthlyCurrencyAmount(
        financialPrediction.now.incomesAmount,
        this.config.currencies
      );
    financialInfo.features.savingCapacityChart.expenses =
      periodicAmountToMonthlyCurrencyAmount(
        financialPrediction.now.expensesAmount,
        this.config.currencies
      );
    financialInfo.features.savingCapacityChart.percentage =
      financialPrediction.now.savingCapacityPercentage;
    financialInfo.features.savingCapacity.percentage =
      financialPrediction.now.savingCapacityPercentage;
    financialInfo.features.otherIncomes.currencyAmount =
      monetaryAmountToCurrencyAmount(
        financialPrediction.now.totalSavings,
        this.config.currencies
      );
  }

  private populateFinancialAdvices(
    financialInfo: FinancialInfo,
    financialAdvices: FinancialAdvice[]
  ) {
    financialAdvices.forEach((financialAdvice) => {
      switch (financialAdvice.type) {
        case 'financial/global':
          financialInfo.situation = extractText(
            financialAdvice.message,
            this.languageService.currentLang
          );
          break;
        case 'financial/incomes':
          financialInfo.features.monthlyIncomes.text = extractText(
            financialAdvice.message,
            this.languageService.currentLang
          );
          break;
        case 'financial/expenses':
          financialInfo.features.monthlyExpenses.text = extractText(
            financialAdvice.message,
            this.languageService.currentLang
          );
          break;
        case 'financial/savingCapacity':
          financialInfo.features.savingCapacity.text = extractText(
            financialAdvice.message,
            this.languageService.currentLang
          );
          break;
        case 'financial/helper':
          financialInfo.features.savingCapacityChart.text = extractText(
            financialAdvice.message,
            this.languageService.currentLang
          );
          break;
        case 'financial/savings':
          financialInfo.features.otherIncomes.text = extractText(
            financialAdvice.message,
            this.languageService.currentLang
          );
          break;
        case 'financial/otherRecommendations':
          financialInfo.recommendations.push(
            extractText(
              financialAdvice.message,
              this.languageService.currentLang
            )
          );
          break;
        default:
          console.error(
            `Unknown financial advice ${JSON.stringify(financialAdvice)}`
          );
      }
    });
  }

  private extractPredictionsAndDispatch(apiResponse: ReportsResponse): any {
    const predictions: PredictionsState = JSON.parse(
      JSON.stringify(initialPredictionsState)
    );
    this.populateDeathPredictions(
      predictions,
      apiResponse.deathProtectionPrediction,
      'current',
      ConcreteInsuranceIds.DEATH_USER
    );
    this.populateDeathPredictions(
      predictions,
      apiResponse.partnerDeathProtectionPrediction,
      'current',
      ConcreteInsuranceIds.DEATH_SPOUSE
    );
    this.populateDeathPredictions(
      predictions,
      apiResponse.deathProtectionPredictionWithoutProducts,
      'default',
      ConcreteInsuranceIds.DEATH_USER
    );
    this.populateDeathPredictions(
      predictions,
      apiResponse.partnerDeathProtectionPredictionWithoutProducts,
      'default',
      ConcreteInsuranceIds.DEATH_SPOUSE
    );

    this.populateEducationPredictions(
      predictions,
      apiResponse.childsEducationPredictions,
      'current'
    );
    this.populateEducationPredictions(
      predictions,
      apiResponse.childsEducationPredictionsWithoutProducts,
      'default'
    );

    this.populateFutureGoalsPredictions(
      predictions,
      apiResponse.futureGoalsPredictions,
      'current'
    );
    this.populateFutureGoalsPredictions(
      predictions,
      apiResponse.futureGoalsPredictionsWithoutProducts,
      'default'
    );

    this.populateRetirementPredictions(
      predictions,
      apiResponse.retirementPrediction,
      'current',
      ConcreteInsuranceIds.RETIREMENT_USER
    );
    this.populateRetirementPredictions(
      predictions,
      apiResponse.partnerRetirementPrediction,
      'current',
      ConcreteInsuranceIds.RETIREMENT_SPOUSE
    );
    this.populateRetirementPredictions(
      predictions,
      apiResponse.retirementPredictionWithoutProducts,
      'default',
      ConcreteInsuranceIds.RETIREMENT_USER
    );
    this.populateRetirementPredictions(
      predictions,
      apiResponse.partnerRetirementPredictionWithoutProducts,
      'default',
      ConcreteInsuranceIds.RETIREMENT_SPOUSE
    );

    this.store.dispatch(
      actions.fullUpdatePredictions({ newPredictionsState: predictions })
    );
    return predictions;
  }

  private populateDeathPredictions(
    predictions: PredictionsState,
    deathProtectionPrediction: DeathProtectionPrediction | undefined,
    prediction: 'current' | 'default',
    user: ConcreteInsuranceId
  ) {
    if (!deathProtectionPrediction) {
      return;
    }
    predictions[prediction][user] = {
      insurance: 'DEATH',
      deathProtection: monetaryAmountToCurrencyAmount(
        deathProtectionPrediction.now.totalRecommendedInsuredAmount,
        this.config.currencies
      ),
      deathPurchasingPower: monetaryAmountToCurrencyAmount(
        deathProtectionPrediction.now.recommendedInsuredPeriodicAmountConcepts
          .purchasingPowerLoss.amount,
        this.config.currencies
      ),
    };
  }

  private populateEducationPredictions(
    predictions: PredictionsState,
    childsEducationPredictions: ChildsEducationPrediction[],
    prediction: 'current' | 'default'
  ) {
    if (!childsEducationPredictions) {
      return;
    }
    childsEducationPredictions.forEach((childEducationPrediction) => {
      if (childEducationPrediction.child.identifier) {
        predictions[prediction][childEducationPrediction.child.identifier] = {
          insurance: 'EDUCATION',
          educationSaving: monetaryAmountToCurrencyAmount(
            childEducationPrediction.atEducationStart
              .recommendedTotalSavingsAmount,
            this.config.currencies
          ),
        };
      }
    });
  }

  private populateFutureGoalsPredictions(
    predictions: PredictionsState,
    futureGoalsPredictions: FutureGoalsPrediction[],
    prediction: 'current' | 'default'
  ) {
    if (!futureGoalsPredictions) {
      return;
    }
    futureGoalsPredictions.forEach((futureGoalsPrediction) => {
      if (
        futureGoalsPrediction.futureGoal.identifier &&
        futureGoalsPrediction.now.recommendedSavingsAmount
      ) {
        predictions[prediction][futureGoalsPrediction.futureGoal.identifier] = {
          insurance: 'SAVING',
          savingSecurity: monetaryAmountToCurrencyAmount(
            futureGoalsPrediction.now.recommendedSavingsAmount,
            this.config.currencies
          ),
        };
      }
    });
  }

  private populateRetirementPredictions(
    predictions: PredictionsState,
    retirementPrediction: RetirementPrediction | undefined,
    prediction: 'current' | 'default',
    user: ConcreteInsuranceId
  ) {
    if (!retirementPrediction) {
      return;
    }
    const retirementPension = periodicAmountToMonthlyCurrencyAmount(
      retirementPrediction.atRetirementYear.salary,
      this.config.currencies
    );
    const retirementPublicPension = periodicAmountToMonthlyCurrencyAmount(
      retirementPrediction.atRetirementYear.publicPension,
      this.config.currencies
    );
    const retirementPurchasingPower: CurrencyAmount = {
      currency:
        retirementPrediction.atRetirementYear.recommendedAdditionalSavingsAmount
          .currency,
      amount:
        retirementPrediction.atRetirementYear.recommendedAdditionalSavingsAmount
          .value,
    };
    const recommendedAdditionalPension = periodicAmountToMonthlyCurrencyAmount(
      retirementPrediction.atRetirementYear.recommendedAdditionalPension,
      this.config.currencies
    );

    predictions[prediction][user] = {
      insurance: 'RETIREMENT',
      retirementPension,
      retirementPublicPension,
      retirementPurchasingPower,
      recommendedAdditionalPension,
    };
  }

  private adjustProductsDataToMatchTemporality(
    products: ProductsPerInsuranceTarget,
    currentLifePlan: LifePlan
  ) {
    const currentLifePlanInsurances = currentLifePlan.insurances;
    if (currentLifePlanInsurances) {
      Object.entries(products).forEach(([id, productsRecommendations]) => {
        const lifePlanInsurance = currentLifePlanInsurances[id];
        if (lifePlanInsurance) {
          const recommendedProduct = productsRecommendations.recommended;
          if (recommendedProduct) {
            adjustProductDataToMatchTemporality(
              recommendedProduct,
              lifePlanInsurance.insured.age,
              this.config.currencyExchange
            );
          }
          productsRecommendations.available?.forEach((p) => {
            adjustProductDataToMatchTemporality(
              p,
              lifePlanInsurance.insured.age,
              this.config.currencyExchange
            );
          });
        }
      });
    }
  }
}
